import * as THREE from 'three';

/**
 * Static placement for one instance of a shared geometry.
 *
 * Rotations are Euler angles (radians) and are applied before the position
 * translation, matching the way a `THREE.Object3D` would compose them.
 */
export interface InstanceTransform {
  position: THREE.Vector3;
  /** Optional Euler rotation; defaults to identity. */
  rotation?: THREE.Euler;
  /** Optional non-uniform scale; defaults to (1, 1, 1). */
  scale?: THREE.Vector3;
}

const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _identityEuler = new THREE.Euler();
const _oneScale = new THREE.Vector3(1, 1, 1);
const _matrix = new THREE.Matrix4();

/**
 * Build a static `InstancedMesh` from a template mesh (or the first mesh found
 * under a template Object3D) placed at the supplied local transforms.
 *
 * Every instance shares the template's geometry and material, so the whole set
 * renders as a single draw call. Shadow flags are copied from the template:
 * clear `castShadow` on the template to opt decorative parts out of the shadow
 * map.
 */
export function instanceStatic(
  source: THREE.Mesh | THREE.Object3D,
  transforms: Array<{ position: THREE.Vector3; rotation?: THREE.Euler; scale?: THREE.Vector3 }>,
  material?: THREE.Material,
): THREE.InstancedMesh {
  const template = findMesh(source);
  const mesh = new THREE.InstancedMesh(
    template.geometry as THREE.BufferGeometry,
    material ?? template.material,
    transforms.length,
  );
  mesh.castShadow = template.castShadow;
  mesh.receiveShadow = template.receiveShadow;

  for (let i = 0; i < transforms.length; i += 1) {
    const transform = transforms[i];
    _position.copy(transform.position);
    _quaternion.setFromEuler(transform.rotation ?? _identityEuler);
    _matrix.compose(_position, _quaternion, transform.scale ?? _oneScale);
    mesh.setMatrixAt(i, _matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

/** Find the first mesh under a template Object3D (or the mesh itself). */
function findMesh(source: THREE.Mesh | THREE.Object3D): THREE.Mesh {
  if ((source as THREE.Mesh).isMesh) return source as THREE.Mesh;

  let found: THREE.Mesh | null = null;
  source.traverse((child) => {
    if (found === null && (child as THREE.Mesh).isMesh) {
      found = child as THREE.Mesh;
    }
  });
  if (found === null) {
    throw new Error('instanceStatic: source must contain at least one mesh');
  }
  return found;
}
